import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import api, { getApiErrorMessage } from '../services/api';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Building2, User, Mail, Phone, MapPin, Briefcase } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export default function SupplierApprovalPage() {
  const { token } = useParams();
  const { systemSettings } = useAuth();
  const tz = systemSettings?.timezone || 'America/Los_Angeles';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processed, setProcessed] = useState(false);
  const [result, setResult] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/api/supplier-portal/approve-external/${token}`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [token]);

  const handleAction = async (action) => {
    setSubmitting(true);
    try {
      const r = await api.post(`/api/supplier-portal/approve-external/${token}`, {
        action, reason, approver_name: 'Email Approver',
      });
      setResult(r.data.message);
      setProcessed(true);
      toast.success(r.data.message);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed'));
    }
    setSubmitting(false);
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent" />
    </div>
  );

  if (!data) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center">
        <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
        <h2 className="text-lg font-bold">Invalid or Expired Link</h2>
        <p className="text-sm text-slate-500 mt-1">This approval link is no longer valid. The supplier may have already been processed.</p>
      </div>
    </div>
  );

  if (processed) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center">
        <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
        <h2 className="text-lg font-bold">{result}</h2>
        <p className="text-sm text-slate-500 mt-1">You can close this page.</p>
      </div>
    </div>
  );

  const a = data.account;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-slate-900 text-white px-6 py-4">
        <h1 className="text-lg font-bold">Supplier Registration Review</h1>
        <p className="text-xs text-slate-400">{data.tenant}</p>
      </div>

      <div className="max-w-2xl mx-auto p-6">
        <div className="border rounded-xl bg-white p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Badge className="bg-amber-100 text-amber-700 text-xs">Pending Approval</Badge>
            <span className="text-xs text-slate-400">Registered {a.registered_at ? new Date(a.registered_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz }) : ''}</span>
          </div>

          <h2 className="text-xl font-bold mb-4">{a.company_name}</h2>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2"><User className="w-4 h-4 text-slate-400" /> <span>{a.contact_name}</span></div>
            <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-slate-400" /> <span>{a.email}</span></div>
            <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400" /> <span>{a.phone || '—'}</span></div>
            <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-slate-400" /> <span>{[a.city, a.state, a.country].filter(Boolean).join(', ') || '—'}</span></div>
            <div className="flex items-center gap-2"><Briefcase className="w-4 h-4 text-slate-400" /> <span>{a.business_type || '—'}</span></div>
            {a.website && <div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-slate-400" /> <span>{a.website}</span></div>}
          </div>

          {a.description && (
            <div className="mt-4 p-3 bg-slate-50 rounded text-sm">{a.description}</div>
          )}
        </div>

        {/* Rejection reason */}
        <div className="border rounded-xl bg-white p-6 mb-6">
          <label className="text-sm font-semibold">Rejection Reason <span className="text-red-500">*</span></label>
          <Textarea value={reason} onChange={e => setReason(e.target.value)}
            placeholder="Provide a reason for rejecting this supplier..." className="mt-2" rows={3} />
          <p className="text-xs text-slate-500 mt-1">Required when rejecting. This reason will be emailed to the supplier.</p>
        </div>

        {/* Action buttons */}
        <div className="flex gap-3">
          <Button onClick={() => handleAction('approve')} disabled={submitting}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 h-12 text-base" data-testid="approve-btn">
            <CheckCircle className="w-5 h-5 mr-2" /> Approve Supplier
          </Button>
          <Button onClick={() => {
            if (!reason.trim()) { toast.error('Rejection reason is required'); return; }
            handleAction('reject');
          }} disabled={submitting}
            variant="outline" className="flex-1 border-red-300 text-red-600 hover:bg-red-50 h-12 text-base" data-testid="reject-btn">
            <XCircle className="w-5 h-5 mr-2" /> Reject
          </Button>
        </div>
      </div>
    </div>
  );
}
